// apps/admin/src/components/ui/Money.tsx
//
// Phase 8.1 Wave 2 Task 06 — `<Money>` composition (USD canonical).
//
// vg-binding: CONTEXT:decisions=P8.D-150,P8.D-151,P8.D-152,P8.D-157,P8.D-186,P8.D-466
// vg-binding: BR:business-rules=BR-8
// vg-binding: CONTEXT:UI-SPEC=§0.1,§12.2
//
//  Currency Display Rule:
//   • Source amount is always USD (stored as integer cents to avoid float
//     drift on the wire).
//   • Display format: `$X.XX USD (≈ {CCY} Y.YY)` when a `displayCcy` is
//     provided AND the live FX hook resolves a rate.
//   • Tooltip i18n key: `admin.common.fxTooltip` — text is locale-driven;
//     the component renders a minimal English fallback when no i18n
//     provider is wired (admin shell does not bundle i18n until Wave 3).
//   • F-01 M1 + F-02 v2 reminder: NO "locked at order creation" wording —
//     the value shown is the LIVE conversion, not a historical snapshot.
//
// Signature: `<Money usdCents={n} displayCcy?="EUR" align?="right" />`.
// Pure presentation — wraps the live FX lookup via `useFxRate()` from
// `apps/admin/src/lib/fxRate.ts`. Table cells SHOULD pass `align="right"`
// for right-aligned numeric columns.

import { useFxRate } from '../../lib/fxRate.js';
import type { JSX } from 'react';

/**
 * Map ISO-4217 currency code → display symbol. Falls back to the code
 * itself when no symbol is registered (defensive — UI never renders an
 * empty currency marker).
 */
const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  VND: '₫',
  CNY: '¥',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
};

/**
 * Map ISO-4217 currency code → decimal precision. Most currencies use
 * 2 decimals; VND/JPY/KRW are minor-unit zero (no fractional display).
 */
const CURRENCY_PRECISION: Record<string, number> = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  VND: 0,
  JPY: 0,
  KRW: 0,
  CNY: 2,
  AUD: 2,
  CAD: 2,
  SGD: 2,
};

function symbolFor(code: string): string {
  return CURRENCY_SYMBOL[code] ?? code;
}

function precisionFor(code: string): number {
  return CURRENCY_PRECISION[code] ?? 2;
}

/**
 * Format a USD cent amount as `$X.XX USD`. Uses `Intl.NumberFormat` for
 * locale-aware thousand separators — pinned to `en-US` because
 * defines USD as the canonical display locale regardless of viewer
 * country (Vietnamese admins still see `$1,250.50 USD`).
 */
export function formatUsd(usdCents: number): string {
  const dollars = usdCents / 100;
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${formatter.format(dollars)} USD`;
}

/**
 * Format a converted amount as `{symbol}{value} {CODE}`. The locale is
 * inferred from the currency code so `VND` renders with vi-VN grouping
 * (no decimals), while `EUR` uses de-DE-style if available. Falls back
 * to en-US for unknown locales (defensive against missing ICU data).
 */
export function formatConverted(amount: number, ccy: string): string {
  const precision = precisionFor(ccy);
  const localeHint =
    ccy === 'VND' ? 'vi-VN' : ccy === 'JPY' ? 'ja-JP' : ccy === 'KRW' ? 'ko-KR' : 'en-US';
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(localeHint, {
      style: 'decimal',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(amount);
  } catch {
    // Defensive: any ICU lookup failure falls back to plain toFixed.
    formatted = amount.toFixed(precision);
  }
  return `${symbolFor(ccy)}${formatted} ${ccy}`;
}

// ---------------------------------------------------------------------------
// Component.
// ---------------------------------------------------------------------------

export interface MoneyProps {
  /** Source amount in USD cents (integer to avoid float drift). */
  usdCents: number;
  /**
   * Optional display currency. When set + FX rate resolves, the
   * companion `(≈ {CCY} Y.YY)` is appended. When unset (or 'USD'),
   * only the USD value is shown.
   */
  displayCcy?: string;
  /** Tailwind alignment hint — defaults to inline left/start alignment. */
  align?: 'left' | 'right' | 'center';
  /** Extra Tailwind class names appended to the root span. */
  className?: string;
  /**
   * When true, render the converted companion inline next to the USD
   * value (default). When false, only the USD value is rendered (used
   * by compact table cells where the tooltip carries the conversion).
   */
  showConverted?: boolean;
}

/**
 * `<Money>` — canonical money-display composition. Always renders the
 * USD source amount; appends `(≈ {CCY} Y.YY)` when a live FX rate
 * resolves. The conversion is LIVE (not historical) per F-01 M1.
 */
export function Money(props: MoneyProps): JSX.Element {
  const {
    usdCents,
    displayCcy,
    align,
    className,
    showConverted = true,
  } = props;

  const targetCcy = (displayCcy ?? 'USD').toUpperCase();
  const { rate, isStale, errorMessage } = useFxRate(targetCcy);

  const usdLabel = formatUsd(usdCents);

  // No conversion needed → only the canonical USD label is rendered.
  const isUsdOnly = targetCcy === 'USD';
  const convertedLabel =
    !isUsdOnly && rate != null && showConverted
      ? `(≈ ${formatConverted((usdCents / 100) * rate, targetCcy)})`
      : null;

  // i18n tooltip — minimal English fallback (admin shell
  // doesn't bundle a translation provider yet; F-02 v2 reminder).
  const tooltipBase =
    'Source amount: USD canonical. Conversion uses live FX rate; ' +
    'value updates as the FX feed refreshes (5-minute cache).';
  const tooltipStaleSuffix = isStale ? ' Rate may be stale.' : '';
  const tooltipErrSuffix = errorMessage
    ? ` FX lookup error: ${errorMessage}.`
    : '';
  const tooltip = `${tooltipBase}${tooltipStaleSuffix}${tooltipErrSuffix}`;

  // Alignment helper — table cells often want `text-right` for numeric.
  const alignClass =
    align === 'right'
      ? 'text-right'
      : align === 'center'
        ? 'text-center'
        : 'text-left';

  return (
    <span
      className={[
        'inline-flex items-baseline gap-1 tabular-nums',
        alignClass,
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="money-cell"
      data-usd-cents={usdCents}
      data-display-ccy={targetCcy}
      title={tooltip}
      aria-label={
        convertedLabel ? `${usdLabel} ${convertedLabel}` : usdLabel
      }
    >
      <span className="font-medium">{usdLabel}</span>
      {convertedLabel ? (
        <span
          className={[
            'text-xs text-pw-text-muted',
            isStale ? 'italic' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-testid="money-converted"
          data-stale={isStale ? 'true' : 'false'}
        >
          {convertedLabel}
        </span>
      ) : null}
    </span>
  );
}

export default Money;
